import { PartialMessage } from '@bufbuild/protobuf';
import {
  deserify,
  getFirebaseApp,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import {
  CreateLiveQuizSessionRequest,
  CreateLiveQuizSessionResponse,
  EndLiveQuizQuestionResponse,
  GetLiveQuizSessionAnalyticsResponse,
  LiveQuizRosterEntry,
  StartLiveQuizQuestionResponse,
} from '@protos/learning_management/lms.liveQuiz.teacher.apis_pb';
import { LiveQuizSession } from '@protos/learning_management/lms.db_pb';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { collection, doc, getFirestore, onSnapshot } from 'firebase/firestore';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import {
  ILiveQuizAnswerStatus,
  ILiveQuizParticipant,
  resetLiveQuiz,
  setCurrentQuestionResponses,
  setCurrentQuestionSessionId,
  setCurrentQuestionStatus,
  setFirestoreCustomToken,
  setJoinedParticipants,
  setLastQuestionStats,
  setLiveQuizAnalytics,
  setLiveQuizQuestions,
  setLiveQuizRoster,
  setLiveQuizSession,
  setQuestionSessions,
} from '../../pages/LiveQuiz/reducer/liveQuiz.slice';
import { setToastInfo } from '../../pages/Home/reducer/homeDashboard.slice';
import {
  readStoredInstanceId,
  removeStoredInstanceId,
  writeStoredInstanceId,
} from '../../pages/Assessment/utils/assessment.utils';
import { Code, ConnectError } from '@bufbuild/connect';

const db = getFirestore(getFirebaseApp());

export type QuestionSessionStatus = 'loading' | 'ready' | 'stale' | 'error';

interface IContextData {
  createLiveQuizSession: (
    req: PartialMessage<CreateLiveQuizSessionRequest>
  ) => Promise<CreateLiveQuizSessionResponse | undefined>;
  pauseLiveQuizSession: (
    liveQuizSessionId: bigint,
    teacherId: bigint
  ) => Promise<boolean>;
  startLiveQuizQuestion: (
    liveQuizSessionId: bigint,
    liveQuizQuestionSessionId: bigint,
    teacherId: bigint
  ) => Promise<StartLiveQuizQuestionResponse | undefined>;
  endLiveQuizQuestion: (
    liveQuizSessionId: bigint,
    liveQuizQuestionSessionId: bigint,
    teacherId: bigint
  ) => Promise<EndLiveQuizQuestionResponse | undefined>;
  endLiveQuizSession: (
    liveQuizSessionId: bigint,
    teacherId: bigint
  ) => Promise<boolean>;
  fetchRoster: (
    liveQuizSessionId: bigint,
    teacherId: bigint
  ) => Promise<LiveQuizRosterEntry[] | undefined>;

  fetchAnalytics: (
    liveQuizSessionId: bigint,
    teacherId: bigint
  ) => Promise<GetLiveQuizSessionAnalyticsResponse['analytics'] | undefined>;
  // Past attempts of THIS assessment for this teacher+section — same idea as
  // Clicker's "Attempt N" selector.
  fetchPreviousSessions: (
    teacherId: bigint,
    schoolClassSectionId: bigint,
    assessmentId: bigint
  ) => Promise<LiveQuizSession[]>;
  // questionSessionStatus: QuestionSessionStatus;
  sessionInstanceInvalid: boolean;
  hasSessionInstance: () => boolean;
  flagSessionStale: () => void;
  detachLiveQuizTab: () => void;
  resetLiveQuizData: () => void;
}

const liveQuizContext = createContext<IContextData | undefined>(undefined);
const { Provider } = liveQuizContext;

interface IProviderProps {
  children: ReactNode;
}

// Teacher-side real-time layer for LiveQuiz. Mirrors
// ClickerAssessmentContextProvider's onSnapshot pattern, but against the new
// liveQuizSessions collection, and authenticates with a custom token scoped
// to this session rather than signInAnonymously (see Firestore security
// design in the LiveQuiz plan — students/teachers never write Firestore
// directly, only the backend does, so this listener is read-only).
// Mounted globally (like ConnectedClassContextProvider/
// ClickerAssessmentContextProvider) and reads its own trigger state from
// redux rather than props, so its listeners stay dormant until
// createLiveQuizSession actually populates live_quiz_session.
export const LiveQuizContextProvider: React.FC<IProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const {
    live_quiz_session,
    firestore_custom_token,
    current_question_session_id,
    question_sessions,
  } = deserify(useAppSelector((state) => state.liveQuiz));
  const firestoreDocId = live_quiz_session?.firestoreDocId;
  const firestoreCustomToken = firestore_custom_token;
  const {
    LmsLiveQuizTeacherAPIServiceV1ClientWithStatusCodeHandler:
      liveQuizTeacherClient,
  } = useCommonServiceClientContext();
  const [authReady, setAuthReady] = useState(false);
  // const [questionSessionStatus, setQuestionSessionStatus] =
  //   useState<QuestionSessionStatus>('loading');
  const [liveQuizSessionDetails, setLiveQuizSessionDetails] = useState<
    LiveQuizSession | undefined
  >();
  const sessionInstanceIdRef = useRef<string | null>(readStoredInstanceId());
  const [sessionInstanceInvalid, setSessionInstanceInvalid] = useState(false);

  const INVALID_INSTANCE_MESSAGE = 'Invalid or expired session instance';
  const isInstanceInvalid = (err: unknown) =>
    err instanceof ConnectError &&
    err.code === Code.Aborted &&
    err.rawMessage.includes(INVALID_INSTANCE_MESSAGE);

  const setInstanceId = (id: string | null) => {
    sessionInstanceIdRef.current = id;
    if (id) {
      writeStoredInstanceId(id);
    } else {
      removeStoredInstanceId();
    }
  };

  const invalidateInstance = (forSilentExit = false) => {
    setInstanceId(null);
    if (!forSilentExit) setSessionInstanceInvalid(true);
  };
  // const currentQuestionSessionIdRef = useRef(current_question_session_id);
  // currentQuestionSessionIdRef.current = current_question_session_id;
  useEffect(() => {
    let cancelled = false;
    setAuthReady(false);

    if (!firestoreCustomToken) return;
    signInWithCustomToken(getAuth(getFirebaseApp()), firestoreCustomToken)
      .then(() => {
        if (!cancelled) setAuthReady(true);
      })
      .catch((err) => {
        if (!cancelled) {
          console.log('LiveQuiz signInWithCustomToken error', err);
          // dispatch(
          //   setToastInfo({
          //     label:
          //       'Could not authenticate with the live quiz session. Please try again.',
          //     variant: 'error',
          //     open: true,
          //   })
          // );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [firestoreCustomToken]);

  // Session doc: current question pointer + status.
  useEffect(() => {
    if (!firestoreDocId || !authReady) return;
    // setQuestionSessionStatus('loading');
    const unsub = onSnapshot(
      doc(db, 'liveQuizSessions', firestoreDocId),
      (snap) => {
        const data = snap.data();
        if (!data) return;

        // const remoteQuestionSessionId: string | undefined =
        //   data.currentQuestionSessionId;
        // const localQuestionSessionId = currentQuestionSessionIdRef.current;
        // if (
        //   localQuestionSessionId &&
        //   remoteQuestionSessionId &&
        //   remoteQuestionSessionId !== localQuestionSessionId
        // ) {
        //   console.log(
        //     'questionSessionStatus :stale',
        //     remoteQuestionSessionId,
        //     localQuestionSessionId
        //   );
        //   setQuestionSessionStatus('stale');
        //   return;
        // }
        dispatch(
          setCurrentQuestionStatus(data.currentQuestionStatus ?? undefined)
        );
        setLiveQuizSessionDetails(data as LiveQuizSession);
        // setQuestionSessionStatus('ready');
      },
      (err) => {
        console.log('LiveQuiz session doc listener error', err);
        // setQuestionSessionStatus('error');
      }
    );
    return () => unsub();
  }, [firestoreDocId, authReady, dispatch]);

  // Roster: who has joined so far, for the live join counter.
  useEffect(() => {
    if (!firestoreDocId || !authReady) return;
    const unsub = onSnapshot(
      collection(db, 'liveQuizSessions', firestoreDocId, 'participants'),
      (snap) => {
        const participants: ILiveQuizParticipant[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            studentId: d.id,
            name: data.name,
            rollNumber: data.rollNumber ?? undefined,
            profilePicUrl: data.profilePicUrl ?? undefined,
            joinedAt: data.joinedAt,
            isOnline: data.isOnline,
          };
        });
        dispatch(setJoinedParticipants(participants));
      },
      (err) => console.log('LiveQuiz participants listener error', err)
    );
    return () => unsub();
  }, [firestoreDocId, authReady, dispatch]);

  // Live "who has answered the current question" — mirrors the hardware
  // Clicker flow's live response indicator. isCorrect is absent on each doc
  // until endLiveQuizQuestion runs (teacher-controlled reveal), so this same
  // listener naturally also drives the post-reveal correct/incorrect marks.
  useEffect(() => {
    if (!firestoreDocId || !authReady || !current_question_session_id) return;
    const unsub = onSnapshot(
      collection(
        db,
        'liveQuizSessions',
        firestoreDocId,
        'questions',
        current_question_session_id,
        'responses'
      ),
      (snap) => {
        const responses: Record<string, ILiveQuizAnswerStatus> = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          responses[d.id] = {
            isCorrect:
              data.isCorrect === undefined
                ? undefined
                : data.isCorrect === 'RESPONSE_CORRECT',
          };
        });
        dispatch(setCurrentQuestionResponses(responses));
      },
      (err) => console.log('LiveQuiz responses listener error', err)
    );
    return () => unsub();
  }, [firestoreDocId, authReady, current_question_session_id, dispatch]);

  useEffect(() => {
    dispatch(setCurrentQuestionResponses({}));
  }, [current_question_session_id, dispatch]);

  const createLiveQuizSession = async (
    req: PartialMessage<CreateLiveQuizSessionRequest>
  ): Promise<CreateLiveQuizSessionResponse | undefined> => {
    try {
      const res = await liveQuizTeacherClient.createLiveQuizSession(req);
      if (res.status === 200 && res.liveQuizSession && res.sessionInstanceId) {
        setInstanceId(res?.sessionInstanceId); // replaces any previous id
        setSessionInstanceInvalid(false);
        dispatch(setLiveQuizSession(res.liveQuizSession));
        dispatch(setQuestionSessions(res.questionSessions));
        dispatch(setLiveQuizQuestions(res.questions));
        dispatch(setFirestoreCustomToken(res.firestoreCustomToken));
        // Resuming a session that was mid-question when paused — the
        // session-doc listener (keyed off firestoreDocId, re-subscribing
        // below) picks currentQuestionStatus back up on its own, but the
        // per-question "who's answered" listener is keyed off this redux
        // field specifically, so it needs setting explicitly here too.
        if (res.liveQuizSession.currentQuestionSessionId) {
          const idStr = res.liveQuizSession.currentQuestionSessionId.toString();
          // Write synchronously BEFORE the Firestore listener (subscribing
          // right after, once firestoreDocId/authReady flip) can possibly
          // fire against this value — avoids the same race on resume.
          // currentQuestionSessionIdRef.current = idStr;
          dispatch(setCurrentQuestionSessionId(idStr));
        }
        return res;
      }
    } catch (err) {
      console.log('createLiveQuizSession error', err);
      dispatch(
        setToastInfo({
          label: 'Could not start the live quiz. Please try again.',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const pauseLiveQuizSession = async (
    liveQuizSessionId: bigint,
    teacherId: bigint
  ) => {
    const sessionInstanceId = sessionInstanceIdRef.current;
    //dont pause a stale session
    if (!sessionInstanceId) return false;
    try {
      const res = await liveQuizTeacherClient.pauseLiveQuizSession({
        teacherId,
        liveQuizSessionId,
        sessionInstanceId,
      });
      return res.status === 200;
    } catch (err) {
      console.log('pauseLiveQuizSession error', err);
      if (isInstanceInvalid(err)) {
        setSessionInstanceInvalid(true);
        return false;
      }
      return false;
    }
  };

  const startLiveQuizQuestion = async (
    liveQuizSessionId: bigint,
    liveQuizQuestionSessionId: bigint,
    teacherId: bigint
  ) => {
    const sessionInstanceId = sessionInstanceIdRef.current;
    if (!sessionInstanceId) {
      invalidateInstance();
      return;
    }
    try {
      const res = await liveQuizTeacherClient.startLiveQuizQuestion({
        teacherId,
        liveQuizSessionId,
        liveQuizQuestionSessionId,
        sessionInstanceId,
      });
      if (res.status === 200) {
        const idStr = liveQuizQuestionSessionId.toString();
        // Synchronous write closes the race: by the time this RPC resolves,
        // the backend has already committed this same id to the Firestore
        // doc, so the doc listener could fire before React re-renders and
        // updates the ref via the render-body assignment above. Writing it
        // here directly means there is no window where Firestore's (correct,
        // already-applied) value looks like a mismatch against our own stale
        // local mirror.
        // currentQuestionSessionIdRef.current = idStr;
        dispatch(setCurrentQuestionSessionId(idStr));
        dispatch(setCurrentQuestionStatus('SESSION_STATUS_STARTED'));
        dispatch(setCurrentQuestionResponses({}));
        // setQuestionSessionStatus('ready');
        return res;
      }
    } catch (err) {
      console.log('startLiveQuizQuestion error', err);
      if (isInstanceInvalid(err)) {
        invalidateInstance();
        return;
      }
      dispatch(
        setToastInfo({
          label: 'Could not start the next question. Please try again.',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const endLiveQuizQuestion = async (
    liveQuizSessionId: bigint,
    liveQuizQuestionSessionId: bigint,
    teacherId: bigint
  ) => {
    const sessionInstanceId = sessionInstanceIdRef.current;
    if (!sessionInstanceId) {
      invalidateInstance();
      return;
    }
    try {
      const res = await liveQuizTeacherClient.endLiveQuizQuestion({
        teacherId,
        liveQuizSessionId,
        liveQuizQuestionSessionId,
        sessionInstanceId,
      });
      if (res.status === 200) {
        dispatch(setCurrentQuestionStatus('SESSION_STATUS_COMPLETED'));
        dispatch(setLastQuestionStats(res.questionStats));
        // setQuestionSessionStatus('ready');
        return res;
      }
    } catch (err) {
      console.log('endLiveQuizQuestion error', err);
      if (isInstanceInvalid(err)) {
        invalidateInstance();
        return;
      }
      dispatch(
        setToastInfo({
          label: 'Could not end the question. Please try again.',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const endLiveQuizSession = async (
    liveQuizSessionId: bigint,
    teacherId: bigint
  ) => {
    const sessionInstanceId = sessionInstanceIdRef.current;
    if (!sessionInstanceId) {
      invalidateInstance();
      return false;
    }
    try {
      const res = await liveQuizTeacherClient.endLiveQuizSession({
        teacherId,
        liveQuizSessionId,
        sessionInstanceId,
      });
      return res.status === 200;
    } catch (err) {
      console.log('endLiveQuizSession error', err);
      if (isInstanceInvalid(err)) {
        invalidateInstance();
        return false;
      }
      dispatch(
        setToastInfo({
          label: 'Could not end the quiz. Please try again.',
          variant: 'error',
          open: true,
        })
      );
      return false;
    }
  };

  const fetchRoster = async (liveQuizSessionId: bigint, teacherId: bigint) => {
    try {
      const res = await liveQuizTeacherClient.getLiveQuizRoster({
        teacherId,
        liveQuizSessionId,
      });
      if (res.status === 200) {
        dispatch(setLiveQuizRoster(res.roster));
        return res.roster;
      }
    } catch (err) {
      console.log('getLiveQuizRoster error', err);
    }
  };

  const fetchAnalytics = async (
    liveQuizSessionId: bigint,
    teacherId: bigint
  ) => {
    try {
      const res = await liveQuizTeacherClient.getLiveQuizSessionAnalytics({
        teacherId,
        liveQuizSessionId,
      });
      if (res.status === 200) {
        dispatch(setLiveQuizAnalytics(res.analytics));
        return res.analytics;
      }
    } catch (err) {
      console.log('getLiveQuizSessionAnalytics error', err);
    }
    return undefined;
  };

  const fetchPreviousSessions = async (
    teacherId: bigint,
    schoolClassSectionId: bigint,
    assessmentId: bigint
  ) => {
    try {
      const res = await liveQuizTeacherClient.getPreviousLiveQuizSessions({
        teacherId,
        schoolClassSectionId,
        assessmentId,
      });
      if (res.status === 200) return res.sessions;
    } catch (err) {
      console.log('getPreviousLiveQuizSessions error', err);
    }
    return [];
  };
  // Stale tab leaving: stop this tab's listeners and drop its id, but leave the
  // shared persisted redux state alone (another tab owns that session).
  const detachLiveQuizTab = () => {
    setInstanceId(null);
    setAuthReady(false); // listeners are keyed on authReady, so they unsubscribe
  };
  const resetLiveQuizData = () => {
    // Reset local state
    setInstanceId(null);
    setAuthReady(false);
    // setQuestionSessionStatus('loading');
    // currentQuestionSessionIdRef.current = undefined; // keep the ref in sync on reset too
    // Reset Redux state
    // dispatch(setCurrentQuestionResponses({}));
    // dispatch(setCurrentQuestionSessionId(''));
    // dispatch(setCurrentQuestionStatus(undefined));
    // dispatch(setJoinedParticipants([]));
    // dispatch(setLastQuestionStats(undefined));
    // dispatch(setLiveQuizAnalytics(undefined));
    // dispatch(setLiveQuizQuestions([]));
    // dispatch(setLiveQuizRoster([]));
    // dispatch(setLiveQuizSession(undefined));
    // dispatch(setQuestionSessions([]));
    // dispatch(setFirestoreCustomToken(''));
    dispatch(resetLiveQuiz());
  };

  const contextValue: IContextData = {
    createLiveQuizSession,
    pauseLiveQuizSession,
    startLiveQuizQuestion,
    endLiveQuizQuestion,
    endLiveQuizSession,
    fetchRoster,
    fetchAnalytics,
    fetchPreviousSessions,
    // questionSessionStatus,
    sessionInstanceInvalid,
    hasSessionInstance: () => !!sessionInstanceIdRef.current,
    flagSessionStale: () => invalidateInstance(),
    detachLiveQuizTab,
    resetLiveQuizData,
  };

  return <Provider value={contextValue}>{children}</Provider>;
};

export const useLiveQuizContext = () => {
  const context = useContext(liveQuizContext);
  if (!context) {
    throw new Error(
      'useLiveQuizContext must be used within a LiveQuizContextProvider'
    );
  }
  return context;
};
